import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Loader2, Upload } from "lucide-react";
import { apiGetProducts, apiCreateProduct, apiUpdateProduct, apiDeleteProduct, apiUpload, resolveImg } from "../../api";

const empty = {
  name: "", gender: "unisex", category: "perfumes", scent: "", price: 0, mrp: 0,
  rating: 4.5, reviews: 0, image: "", badges: [], sizes: ["50ml", "100ml"],
  description: "", stock: 100, isActive: true,
  notes: { top: [], heart: [], base: [] },
};

const ProductForm = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(() => ({ ...empty, ...initial, notes: initial?.notes || empty.notes }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await apiUpload(file);
      set("image", url);
    } catch {
      setError("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setError("");
    if (!form.name) { setError("Name is required"); return; }
    setSaving(true);
    const payload = {
      ...form,
      price: Number(form.price), mrp: Number(form.mrp), reviews: Number(form.reviews),
      rating: Number(form.rating), stock: Number(form.stock),
      badges: Array.isArray(form.badges) ? form.badges : String(form.badges).split(",").map((s) => s.trim()).filter(Boolean),
      sizes: Array.isArray(form.sizes) ? form.sizes : String(form.sizes).split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (initial?.id) await apiUpdateProduct(initial.id, payload);
      else await apiCreateProduct(payload);
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const I = (label, key, props = {}) => (
    <div>
      <label className="block text-xs font-medium text-neutral-600 mb-1">{label}</label>
      <input value={form[key]} onChange={(e) => set(key, e.target.value)} {...props}
        className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#6b0f1a]" />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-sm w-full max-w-2xl my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <h3 className="font-serif text-xl">{initial?.id ? "Edit Product" : "Add Product"}</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-neutral-500" /></button>
        </div>
        <div className="p-6 grid grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
          <div className="col-span-2">{I("Name", "name")}</div>
          {I("Scent", "scent")}
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Category</label>
            <select value={form.category} onChange={(e) => set("category", e.target.value)} className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm">
              {["perfumes", "attars", "gift-sets", "bakhoor"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Gender</label>
            <select value={form.gender} onChange={(e) => set("gender", e.target.value)} className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm">
              {["men", "women", "unisex"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {I("Price (₹)", "price", { type: "number" })}
          {I("MRP (₹)", "mrp", { type: "number" })}
          {I("Rating", "rating", { type: "number", step: "0.1" })}
          {I("Reviews", "reviews", { type: "number" })}
          {I("Stock", "stock", { type: "number" })}
          {I("Badges (comma-sep)", "badges")}
          {I("Sizes (comma-sep)", "sizes")}
          <div className="col-span-2">
            <label className="block text-xs font-medium text-neutral-600 mb-1">Description</label>
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3}
              className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#6b0f1a]" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-neutral-600 mb-1">Image URL or Upload</label>
            <div className="flex gap-2">
              <input value={form.image} onChange={(e) => set("image", e.target.value)} placeholder="https://..."
                className="flex-1 border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#6b0f1a]" />
              <label className="flex items-center gap-1.5 border border-neutral-300 rounded-sm px-3 text-sm cursor-pointer hover:bg-neutral-50">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                <input type="file" accept="image/*" className="hidden" onChange={handleUpload} />
              </label>
            </div>
            {form.image && <img src={resolveImg(form.image)} alt="preview" className="mt-2 h-24 w-20 object-cover rounded-sm bg-neutral-100" />}
          </div>
        </div>
        {error && <p className="px-6 text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-neutral-200">
          <button onClick={onClose} className="px-5 py-2 text-sm border border-neutral-300 rounded-sm hover:bg-neutral-50">Cancel</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 text-sm bg-[#6b0f1a] text-[#e9c977] rounded-sm hover:bg-[#560b15] flex items-center gap-2 disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminProducts = () => {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => apiGetProducts({ admin: true }).then(setList).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await apiDeleteProduct(id);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl text-neutral-900">Products ({list.length})</h1>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-2 bg-[#6b0f1a] text-[#e9c977] px-4 py-2.5 text-sm font-medium rounded-sm hover:bg-[#560b15]">
          <Plus className="h-4 w-4" /> Add Product
        </button>
      </div>
      <div className="bg-white rounded-sm border border-neutral-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-neutral-500 bg-neutral-50 border-b border-neutral-200">
              <th className="py-3 px-4 font-medium">Product</th>
              <th className="py-3 px-4 font-medium">Category</th>
              <th className="py-3 px-4 font-medium">Price</th>
              <th className="py-3 px-4 font-medium">Stock</th>
              <th className="py-3 px-4 font-medium">Active</th>
              <th className="py-3 px-4 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                <td className="py-3 px-4">
                  <div className="flex items-center gap-3">
                    <img src={resolveImg(p.image)} alt={p.name} className="h-12 w-10 object-cover rounded-sm bg-neutral-100" />
                    <span className="font-medium text-neutral-800">{p.name}</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-neutral-600 capitalize">{p.category}</td>
                <td className="py-3 px-4">₹{p.price.toLocaleString("en-IN")}</td>
                <td className="py-3 px-4">{p.stock}</td>
                <td className="py-3 px-4">{p.isActive ? <span className="text-green-600">Yes</span> : <span className="text-red-500">No</span>}</td>
                <td className="py-3 px-4">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => { setEditing(p); setShowForm(true); }} className="p-1.5 hover:text-[#6b0f1a]"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(p.id)} className="p-1.5 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showForm && <ProductForm initial={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
};

export default AdminProducts;
